import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { PageHeader, EmptyState } from "../components/common";
import { itDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Clock } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const ymd = (d) => d.toLocaleDateString("sv-SE");
const fmtH = (h) => `${Number(h).toLocaleString("it-IT", { maximumFractionDigits: 2 })} h`;

function range(mode, cur) {
  if (mode === "week") {
    const s = new Date(cur); s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
    const e = new Date(s); e.setDate(e.getDate() + 6);
    return [s, e, `${s.getDate()} ${MONTHS[s.getMonth()].slice(0, 3)} – ${e.getDate()} ${MONTHS[e.getMonth()].slice(0, 3)} ${e.getFullYear()}`];
  }
  const s = new Date(cur.getFullYear(), cur.getMonth(), 1), e = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
  return [s, e, `${MONTHS[s.getMonth()]} ${s.getFullYear()}`];
}

export default function OrePersonale() {
  const [mode, setMode] = useState("week");
  const [cur, setCur] = useState(new Date());
  const [collab, setCollab] = useState("");
  const [collabs, setCollabs] = useState([]);
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [ask, setAsk] = useState(false);
  const [s, e, label] = range(mode, cur);

  const load = async () => {
    const { data } = await api.get(`/work-hours?date_from=${ymd(s)}&date_to=${ymd(e)}${collab ? `&collaborator_id=${collab}` : ""}`);
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, ymd(s), collab]);
  useEffect(() => { api.get("/collaborators").then((r) => setCollabs(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (!dlg) { setAsk(false); const t = setTimeout(() => { if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = ""; }, 200); return () => clearTimeout(t); } }, [dlg]);

  const totals = useMemo(() => {
    const m = {};
    items.forEach((i) => { m[i.collaborator_id] = m[i.collaborator_id] || { name: i.collaborator_name, h: 0, n: 0 }; m[i.collaborator_id].h += i.hours; m[i.collaborator_id].n += 1; });
    return Object.entries(m).sort((a, b) => b[1].h - a[1].h);
  }, [items]);
  const grand = items.reduce((a, i) => a + i.hours, 0);

  const shift = (n) => { const d = new Date(cur); if (mode === "week") d.setDate(d.getDate() + 7 * n); else d.setMonth(d.getMonth() + n, 1); setCur(d); };
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); setDlg(null); load(); } catch (err) { toast.error(err?.response?.data?.detail || "Operazione non riuscita"); } };
  const save = () => {
    if (!dlg.collaborator_id) return toast.error("Scegli il collaboratore");
    run(() => dlg.id ? api.put(`/work-hours/${dlg.id}`, dlg) : api.post("/work-hours", dlg), "Ore salvate");
  };

  return (
    <div>
      <PageHeader title="Orario del personale" subtitle="Ore lavorate per collaboratore, con totali per settimana o mese">
        <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-hours" onClick={() => { if (!collabs.length) return toast.error("Aggiungi prima un collaboratore nella sezione Squadre"); setDlg({ date: ymd(new Date()), collaborator_id: collab || "" }); }}><Plus size={16} className="mr-1" /> Registra ore</Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1">
          {[["week", "Settimana"], ["month", "Mese"]].map(([v, l]) => <button key={v} data-testid={`hours-mode-${v}`} onClick={() => setMode(v)} className={`text-xs px-3 py-1.5 rounded-full border ${mode === v ? "bg-ink text-white" : "bg-white text-muted-foreground"}`}>{l}</button>)}
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" data-testid="hours-prev" onClick={() => shift(-1)}><ChevronLeft size={16} /></Button>
          <span className="font-head font-semibold text-sm min-w-[170px] text-center" data-testid="hours-period">{label}</span>
          <Button variant="outline" size="icon" data-testid="hours-next" onClick={() => shift(1)}><ChevronRight size={16} /></Button>
        </div>
        <div className="w-full sm:w-56">
          <Select value={collab || "all"} onValueChange={(v) => setCollab(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="hours-filter-collab"><SelectValue /></SelectTrigger>
            <SelectContent onCloseAutoFocus={(ev) => ev.preventDefault()}><SelectItem value="all">Tutti i collaboratori</SelectItem>{collabs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="card-soft p-4 lg:order-2 h-fit" data-testid="hours-totals">
          <p className="text-xs uppercase text-muted-foreground font-medium">Totale periodo</p>
          <p className="font-head text-3xl font-extrabold text-brand mb-3" data-testid="hours-grand-total">{fmtH(grand)}</p>
          {totals.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna ora registrata.</p> : totals.map(([id, t]) => (
            <div key={id} data-testid={`hours-total-${id}`} className="flex items-center justify-between py-1.5 border-b border-border last:border-0 text-sm">
              <span className="truncate">{t.name} <span className="text-muted-foreground text-xs">· {t.n} gg</span></span><span className="font-semibold">{fmtH(t.h)}</span>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 lg:order-1">
          {items.length === 0 ? <EmptyState title="Nessuna registrazione nel periodo" hint="Usa + Registra ore per aggiungerne una" /> : (
            <div className="space-y-2">
              {items.map((i) => (
                <button key={i.id} data-testid={`hours-row-${i.id}`} onClick={() => setDlg({ ...i })} className="w-full card-soft p-3 text-left flex items-center gap-3 lift">
                  <Clock size={18} className="text-brand shrink-0" />
                  <div className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{i.collaborator_name}</p><p className="text-xs text-muted-foreground truncate">{itDate(i.date)}{i.notes ? ` · ${i.notes}` : ""}</p></div>
                  <span className="font-head font-bold">{fmtH(i.hours)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {dlg && (
        <Dialog open onOpenChange={(o) => { if (!o) setDlg(null); }}>
          <DialogContent data-testid="hours-dialog">
            <DialogHeader><DialogTitle className="font-head">{dlg.id ? "Correggi registrazione" : "Registra ore"}</DialogTitle><DialogDescription className="sr-only">Compila e salva</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Collaboratore *</Label>
                <Select value={dlg.collaborator_id || ""} onValueChange={(v) => setDlg({ ...dlg, collaborator_id: v })}>
                  <SelectTrigger data-testid="hours-collab"><SelectValue placeholder="Scegli" /></SelectTrigger>
                  <SelectContent onCloseAutoFocus={(ev) => ev.preventDefault()}>{collabs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Data *</Label><Input data-testid="hours-date" type="date" value={dlg.date || ""} onChange={(ev) => setDlg({ ...dlg, date: ev.target.value })} /></div>
                <div><Label className="text-xs">Ore svolte *</Label><Input data-testid="hours-hours" type="number" step="0.25" min="0" max="24" value={dlg.hours ?? ""} onChange={(ev) => setDlg({ ...dlg, hours: ev.target.value })} placeholder="es. 8" /></div>
              </div>
              <div><Label className="text-xs">Note</Label><Textarea data-testid="hours-notes" rows={2} value={dlg.notes || ""} onChange={(ev) => setDlg({ ...dlg, notes: ev.target.value })} placeholder="es. cantiere Rossi, straordinario" /></div>
            </div>
            <DialogFooter className="flex-row gap-2 sm:justify-between">
              {dlg.id ? (ask ? <Button variant="destructive" data-testid="hours-delete-confirm" onClick={() => run(() => api.delete(`/work-hours/${dlg.id}`), "Registrazione eliminata")}>Conferma eliminazione</Button>
                : <Button variant="outline" className="text-destructive border-destructive/40" data-testid="hours-delete" onClick={() => setAsk(true)}><Trash2 size={15} /></Button>) : <span />}
              <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="hours-save" onClick={save}>Salva</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
